import type { Point2 } from '../geometry/point';
import type { QuarterTurns, ViewportSize, ViewportState } from './types';

/**
 * `[cos, sin]` per quarter turn, as exact integers.
 *
 * Looked up rather than computed, so that rotating four times returns a viewport
 * bit-identical to the one you started with — `Math.cos(Math.PI / 2)` is 6e-17, not
 * zero, and that error compounds through every world↔screen round trip.
 */
const ROTATION: Record<QuarterTurns, readonly [number, number]> = {
  0: [1, 0],
  1: [0, 1],
  2: [-1, 0],
  3: [0, -1],
};

/**
 * Rotates a screen-space offset (y-down) clockwise by `quarterTurns`.
 *
 * Applied to offsets from the container centre, which is the pivot every rotation
 * in this viewer turns about.
 */
export const rotateScreenOffset = (
  offset: Point2,
  quarterTurns: QuarterTurns,
): Point2 => {
  const [cos, sin] = ROTATION[quarterTurns];
  const [x, y] = offset;
  return [cos * x - sin * y, sin * x + cos * y];
};

/** Inverse of `rotateScreenOffset`. */
export const unrotateScreenOffset = (
  offset: Point2,
  quarterTurns: QuarterTurns,
): Point2 => {
  const [cos, sin] = ROTATION[quarterTurns];
  const [x, y] = offset;
  return [cos * x + sin * y, cos * y - sin * x];
};

/** Maps a world point to container-relative screen pixels. */
export const worldToScreen = (
  world: Point2,
  viewport: ViewportState,
  size: ViewportSize,
): Point2 => {
  const [worldX, worldY] = world;
  const [centerX, centerY] = viewport.center;

  // Unrotated screen offset from the container centre; y flips because world y is
  // up and screen y is down.
  const [offsetX, offsetY] = rotateScreenOffset(
    [(worldX - centerX) * viewport.scale, (centerY - worldY) * viewport.scale],
    viewport.quarterTurns,
  );

  return [offsetX + size.width / 2, offsetY + size.height / 2];
};

/** Maps container-relative screen pixels back to a world point. */
export const screenToWorld = (
  screen: Point2,
  viewport: ViewportState,
  size: ViewportSize,
): Point2 => {
  const [screenX, screenY] = screen;
  const [centerX, centerY] = viewport.center;

  const [offsetX, offsetY] = unrotateScreenOffset(
    [screenX - size.width / 2, screenY - size.height / 2],
    viewport.quarterTurns,
  );

  return [
    centerX + offsetX / viewport.scale,
    centerY - offsetY / viewport.scale,
  ];
};

/**
 * Converts a screen-pixel delta into the world-space delta it represents.
 *
 * Rotation-aware, which is what keeps a drag feeling direct: the content follows
 * the pointer whichever way the view is turned.
 */
export const screenDeltaToWorld = (
  deltaX: number,
  deltaY: number,
  viewport: ViewportState,
): Point2 => {
  const [offsetX, offsetY] = unrotateScreenOffset(
    [deltaX, deltaY],
    viewport.quarterTurns,
  );
  return [offsetX / viewport.scale, -offsetY / viewport.scale];
};
